import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Droplets, RefreshCw, Thermometer, Waves, Wind } from "lucide-react";

const W_URL =
    "https://api.open-meteo.com/v1/forecast?latitude=-23.4332&longitude=-45.0709&current=temperature_2m,apparent_temperature,relative_humidity_2m,weather_code,wind_speed_10m&timezone=America%2FSao_Paulo";
const M_URL =
    "https://marine-api.open-meteo.com/v1/marine?latitude=-23.4332&longitude=-45.0709&current=wave_height&timezone=America%2FSao_Paulo&cell_selection=sea";

const WMO = {
    0: "Céu limpo",
    1: "Predomínio de sol",
    2: "Parcialmente nublado",
    3: "Nublado",
    45: "Neblina",
    48: "Neblina",
    51: "Garoa fraca",
    53: "Garoa",
    55: "Garoa forte",
    61: "Chuva fraca",
    63: "Chuva",
    65: "Chuva forte",
    80: "Pancadas isoladas",
    81: "Pancadas de chuva",
    82: "Pancadas fortes",
    95: "Tempestade",
    96: "Tempestade com granizo",
    99: "Tempestade com granizo",
};

const metric = (value, unit) => Number.isFinite(value) ? `${value}${unit}` : "—";

function Skeleton() {
    return (
        <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
            {[...Array(4)].map((_, i) => (
                <div
                    key={i}
                    className="h-40 animate-pulse rounded-3xl border border-ink/10 bg-deep"
                />
            ))}
        </div>
    );
}

export default function Clima() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const hasDataRef = useRef(false);
    const requestRef = useRef(0);
    const controllerRef = useRef(null);

    const load = useCallback(async () => {
        // Cancelar atualização anterior evita respostas antigas substituírem dados novos.
        const requestId = ++requestRef.current;
        controllerRef.current?.abort();
        const ctrl = new AbortController();
        controllerRef.current = ctrl;
        const timer = setTimeout(() => ctrl.abort(), 10000);
        setRefreshing(true);
        setError(null);
        setLoading(!hasDataRef.current);

        // Ondas são opcionais. Falha do serviço marítimo nunca derruba o clima.
        const marinePromise = fetch(M_URL, { signal: ctrl.signal })
            .then((res) => {
                if (!res.ok) throw new Error("marine");
                return res.json();
            })
            .catch(() => null);

        try {
            const weatherResponse = await fetch(W_URL, { signal: ctrl.signal });
            if (!weatherResponse.ok) throw new Error("weather");
            const weatherData = await weatherResponse.json();
            if (!weatherData?.current || !Number.isFinite(weatherData.current.temperature_2m)) {
                throw new Error("missing weather data");
            }
            if (requestId !== requestRef.current) return;

            hasDataRef.current = true;
            setData({ w: weatherData.current, m: null });
            setLoading(false);

            const marineData = await marinePromise;
            if (requestId !== requestRef.current) return;
            const wave = marineData?.current?.wave_height;
            setData({
                w: weatherData.current,
                m: Number.isFinite(wave) ? marineData.current : null,
            });
        } catch {
            if (requestId !== requestRef.current) return;
            setError(hasDataRef.current
                ? "Não foi possível atualizar agora. Exibindo a última previsão disponível."
                : "Não foi possível carregar as condições agora.");
            setLoading(false);
        } finally {
            clearTimeout(timer);
            if (requestId === requestRef.current) {
                controllerRef.current = null;
                setRefreshing(false);
            }
        }
    }, []);

    useEffect(() => {
        load();
        // A previsão se renova enquanto a página está em uso, sem APIs pagas.
        const interval = setInterval(() => {
            if (!document.hidden) load();
        }, 10 * 60 * 1000);
        return () => {
            clearInterval(interval);
            requestRef.current += 1;
            controllerRef.current?.abort();
        };
    }, [load]);

    const score = useMemo(() => {
        const wind = data?.w?.wind_speed_10m;
        const wave = data?.m?.wave_height;
        if (!Number.isFinite(wind) || !Number.isFinite(wave)) return null;
        const sW = Math.max(0, 100 - (wind / 40) * 100);
        const sV =
            wave == null ? 60 : Math.max(0, 100 - (wave / 2) * 100);
        return Math.round((sW + sV) / 2);
    }, [data]);

    const status = useMemo(() => {
        if (score == null) return { label: "SEM DADOS", color: "#B8C5D1" };
        if (score >= 70)
            return { label: "IDEAL", color: "#00E5A3" };
        if (score >= 45)
            return { label: "MODERADA", color: "#FFD166" };
        return { label: "ATENÇÃO", color: "#FF4B4B" };
    }, [score]);

    const cards = data?.w
        ? [
              {
                  id: "temp",
                  icon: Thermometer,
                  label: "Temperatura",
                  value: metric(data.w.temperature_2m, "°C"),
                  sub: WMO[data.w.weather_code] ?? "Condições variáveis",
              },
              {
                  id: "vento",
                  icon: Wind,
                  label: "Vento",
                  value: metric(data.w.wind_speed_10m, " km/h"),
                  sub: "medido a 10 m de altura",
              },
              {
                  id: "ondas",
                  icon: Waves,
                  label: "Ondas",
                  value: metric(data.m?.wave_height, " m"),
                  sub: data.m ? "altura significativa no mar" : "dados marítimos indisponíveis no momento",
              },
              {
                  id: "umidade",
                  icon: Droplets,
                  label: "Umidade",
                  value: metric(data.w.relative_humidity_2m, "%"),
                  sub: `sensação de ${metric(data.w.apparent_temperature, "°C")}`,
              },
          ]
        : [];

    return (
        <section id="clima" className="relative py-24 lg:py-32">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <div className="flex flex-wrap items-end justify-between gap-6">
                    <div className="max-w-2xl">
                        <p className="font-mono2 text-[11px] font-semibold uppercase tracking-[0.3em] text-neon-teal">
                            Clima ao vivo
                        </p>
                        <h2 className="mt-4 font-display text-3xl font-black uppercase leading-tight text-ink sm:text-4xl lg:text-5xl">
                            Ubatuba <span className="text-stroke">agora</span>
                        </h2>
                    </div>
                    <button
                        onClick={load}
                        disabled={refreshing}
                        data-testid="weather-refresh-button"
                        className="inline-flex items-center gap-2 rounded-full border border-ink/25 px-5 py-3 font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-dim transition-colors hover:border-neon-cyan hover:text-neon-cyan disabled:opacity-40"
                    >
                        <RefreshCw
                            className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`}
                        />
                        Atualizar
                    </button>
                </div>

                {loading && <Skeleton />}

                {!loading && !data && error && (
                    <div
                        data-testid="weather-error"
                        className="mt-12 rounded-3xl border border-neon-coral/30 bg-deep p-10 text-center"
                    >
                        <p className="text-dim">{error}</p>
                        <button
                            onClick={load}
                            data-testid="weather-retry-button"
                            className="mt-5 rounded-full bg-neon-cyan px-6 py-3 font-display text-[10px] font-extrabold uppercase tracking-widest text-ocean"
                        >
                            Tentar de novo
                        </button>
                    </div>
                )}

                {data && (
                    <>
                        <div className="mt-12 grid grid-cols-2 gap-4 lg:grid-cols-4">
                            {cards.map((c, i) => (
                                <motion.div
                                    key={c.id}
                                    initial={{ opacity: 0, y: 26 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, amount: 0.15 }}
                                    transition={{
                                        duration: 0.6,
                                        delay: i * 0.08,
                                        ease: [0.16, 1, 0.3, 1],
                                    }}
                                    data-testid={`weather-${c.id}-display`}
                                    className="rounded-3xl border border-ink/10 bg-deep p-6 transition-colors duration-300 hover:border-neon-cyan/40"
                                >
                                    <c.icon className="h-5 w-5 text-neon-cyan" />
                                    <p className="mt-5 font-mono2 text-[10px] font-semibold uppercase tracking-[0.25em] text-dim">
                                        {c.label}
                                    </p>
                                    <p className="mt-2 font-display text-3xl font-black text-ink">
                                        {c.value}
                                    </p>
                                    <p className="mt-2 text-xs text-dim">
                                        {c.sub}
                                    </p>
                                </motion.div>
                            ))}
                        </div>

                        {error && <p role="status" className="mt-4 text-sm text-neon-coral">{error}</p>}
                        {data.w.time && (
                            <p className="mt-4 text-xs text-dim">
                                Previsão referente a {data.w.time.replace("T", " às ")} (horário de Ubatuba).
                            </p>
                        )}

                        <motion.div
                            initial={{ opacity: 0, y: 26 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.15 }}
                            transition={{ duration: 0.7, delay: 0.2 }}
                            data-testid="weather-status-gauge"
                            className="mt-6 rounded-3xl border border-ink/10 bg-deep p-7 sm:p-9"
                        >
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <p className="font-mono2 text-[11px] font-semibold uppercase tracking-[0.3em] text-dim">
                                    Condição para pilotagem
                                </p>
                                <span
                                    className="rounded-full px-5 py-2 font-display text-xs font-extrabold uppercase tracking-widest text-ocean"
                                    style={{ background: status?.color }}
                                >
                                    {status?.label ?? "—"}
                                </span>
                            </div>
                            {score != null && (
                                <div className="mt-6 h-2.5 overflow-hidden rounded-full bg-ocean">
                                    <motion.div
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${score}%` }}
                                        viewport={{ once: true, amount: 0.15 }}
                                        transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                                        className="h-full rounded-full"
                                        style={{ background: status?.color }}
                                    />
                                </div>
                            )}
                            <p className="mt-4 text-xs leading-relaxed text-dim">
                                {score == null
                                    ? "Sem dados suficientes de vento e ondas para estimar condições. Consulte a equipe antes do passeio."
                                    : "Indicador estimado a partir de vento e ondulação — não substitui a avaliação de segurança da equipe antes do passeio."}
                            </p>
                        </motion.div>

                        <p className="mt-6 font-mono2 text-[10px] uppercase tracking-[0.2em] text-dim/70">
                            Dados por{" "}
                            <a
                                href="https://open-meteo.com/"
                                target="_blank"
                                rel="noreferrer"
                                className="underline decoration-neon-cyan/50 underline-offset-4 hover:text-neon-cyan"
                            >
                                Open-Meteo.com
                            </a>
                        </p>
                    </>
                )}
            </div>
        </section>
    );
}
